import { NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ButtonComponent } from '@zeiss/beyond-online-angular';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { LoadingAnimationService } from 'src/app/modules/loading-animation/loading-animation.service';
import { CartStore } from 'src/app/state-management/stores/cart.store';
import { ECPStore } from 'src/app/state-management/stores/ecp.store';
import { OrderStore } from 'src/app/state-management/stores/order.store';
import {
	IconComponent,
	PopUpConfig,
	PopUpRef,
	PopUpService,
	ThreeSectionDrawerLayoutComponent,
	ToastService,
	ToastType,
} from 'vis-ods-angular';
import { ConfigurationStore } from '../../../../app/state-management/stores/configuration.store';
import { LoadingAnimationDirective } from '../../../modules/loading-animation/loading-animation.directive';
import { RoutesEnum } from '../../../routing/routes.enum';
import { SummaryRoutesEnum } from '../../../routing/summary-outlet-routes.enum';
import { ProductSummaryComponent } from '../../../shared/components/product-summary/product-summary.component';
import { SingleOrderSubmitSuccessPopupComponent } from '../../../shared/components/single-order-submit-success-popup/single-order-submit-success-popup.component';
import { DateTimeFormatPipe } from '../../../shared/pipes/dateTime-format.pipe';
import { UserPermissionPipe } from '../../../shared/pipes/user-permission.pipe';
import { RolesEnum } from '../../../types/enums/roles-state.enum';

@Component({
	selector: 'app-summary',
	templateUrl: './summary.component.html',
	imports: [
		TranslocoModule,
		ThreeSectionDrawerLayoutComponent,
		IconComponent,
		NgIf,
		LoadingAnimationDirective,
		ProductSummaryComponent,
		ButtonComponent,
		UserPermissionPipe,
		DateTimeFormatPipe,
	],
})
export class SummaryComponent implements OnInit, OnDestroy {
	private destroyed$ = new Subject<void>();

	private readonly orderStore = inject(OrderStore);
	private readonly ecpStore = inject(ECPStore);
	private readonly cartStore = inject(CartStore);
	private readonly router = inject(Router);
	private readonly toastService = inject(ToastService);
	private readonly popUpService = inject(PopUpService);
	private readonly translocoService = inject(TranslocoService);
	private readonly loadingAnimationService = inject(LoadingAnimationService);
	private readonly configurationStore = inject(ConfigurationStore);

	public popUpConfig = inject(PopUpConfig);
	public popUp? = inject(PopUpRef);

	public $order = this.orderStore.order;
	public $isEditMode = this.orderStore.isEditMode;
	public $selectedCartItemId = this.orderStore.selectedCartItemId;
	public $selectedShipToStore = this.orderStore.selectedShipToStore;
	public $shipToStoresLoading = this.ecpStore.isLoading('shipToStores');
	public $shipToContactsLoading = this.ecpStore.isLoading('shipToContacts');
	public $shipToStores = this.ecpStore.shipToStores;

	public readonly $isBlockedFromOrdering = this.configurationStore.isBlockedFromOrdering;
	public readonly blockRemoveDate =
		this.configurationStore.settings()?.block_submit_order_remove_date;

	public readonly rolesEnum = RolesEnum;
	public $submitting = computed(
		() => this.orderStore.isLoading('submit')() || this.cartStore.isLoading('addCartItem')(),
	);

	public $isUserChangingCart = computed(
		() => this.cartStore.isLoading('addCartItem')() || this.cartStore.isLoading('updateCartItem')(),
	);

	async ngOnInit() {
		if (this.$shipToStores().length === 0) {
			await this.ecpStore.fetchShipToStores();
		}
		if (!this.$order().shipTo) {
			this.setDefaultShipTo();
		}
		this.popUpConfig.disableBackdropClose?.next(false);
	}

	ngOnDestroy(): void {
		this.destroyed$.next();
		this.destroyed$.complete();
	}

	openPrintDialog(): void {
		window.print();
	}

	scrollToTop() {
		window.scrollTo({
			top: 0,
			left: 0,
			behavior: 'smooth',
		});
	}

	async addToCart() {
		if (this.$submitting() || this.showShipToStoreMissingError()) return;

		// Restrict user from navigating while submitting order
		this.popUpConfig.disableBackdropClose?.next(true);
		this.popUpConfig.enableCloseButton?.next(false);

		this.showShipToStoreMissingError();

		this.loadingAnimationService.open();
		if (this.$isEditMode()) {
			// ! because we know that the selectedCartItemId is not undefined; problem with oe-backend types
			await this.cartStore.updateCartItem(this.$selectedCartItemId()!, this.$order());
			if (this.cartStore.hasError('updateCartItem')()) return;
			this.orderStore.reset();

			this.toastService.addToast({
				automaticDeleteTime: 3000,
				variant: ToastType.success,
				headline: this.translocoService.translate(
					'shoppingCart.subcomponents.cartItemUpdatedToast.headlines.title',
				),
				description: this.translocoService.translate(
					'shoppingCart.subcomponents.cartItemUpdatedToast.texts.message',
				),
			});
			await this.router.navigateByUrl(`/${RoutesEnum.shoppingCart}`);
			this.popUp?.close();
		} else {
			await this.cartStore.addCartItem(this.$order());
			if (this.cartStore.hasError('addCartItem')()) return;
			this.router.navigate([{ outlets: { summary: [SummaryRoutesEnum.successMessage] } }]);
		}
		this.loadingAnimationService.close();
	}

	async submitOrder() {
		if (this.$submitting() || this.showShipToStoreMissingError()) return;

		// Restrict user from navigating while submitting order
		this.popUpConfig.disableBackdropClose?.next(true);
		this.popUpConfig.enableCloseButton?.next(false);

		this.loadingAnimationService.open();
		if (this.$isEditMode()) {
			// ! because we know that the cartItem.id is not undefined; problem with oe-backend types
			await this.cartStore.updateCartItem(this.$selectedCartItemId()!, this.$order());
			if (this.cartStore.hasError('updateCartItem')()) return;

			await this.orderStore.submitOrdersByIds([this.$selectedCartItemId()!]);
			if (this.orderStore.hasError('submit')()) return;

			this.orderStore.reset();
			await this.router.navigate([RoutesEnum.shoppingCart]);
			this.scrollToTop();
		} else {
			await this.orderStore.submitOrders([this.$order()]);
			if (this.orderStore.hasError('submit')()) return;
			await this.router.navigate([RoutesEnum.home]);
		}

		this.loadingAnimationService.close();
		this.orderStore.reset();
		this.popUp?.close();
		this.popUpService.open(SingleOrderSubmitSuccessPopupComponent, {
			enableCloseButton: new BehaviorSubject<boolean>(true),
			disableBackdropClose: new BehaviorSubject<boolean>(true),
		});
	}

	showShipToStoreMissingError() {
		if (!this.$selectedShipToStore()) {
			this.toastService
				.addToast({
					automaticDeleteTime: 10000,
					variant: ToastType.error,
					headline: this.translocoService.translate('errors.ShipToMissing.headline'),
					description: this.translocoService.translate('errors.ShipToMissing.text'),
					buttonLabel: this.translocoService.translate('errors.ShipToMissing.buttonLabel'),
					isButtonVisible: true,
					closeAfterButtonClick: true,
				})
				.buttonClicked.pipe(takeUntil(this.destroyed$))
				.subscribe(() => {
					this.routeToStoreSelector();
				});
			return true;
		}
		return false;
	}

	routeToStoreSelector() {
		this.popUpConfig.disableBackdropClose?.next(true);
		this.router.navigate([{ outlets: { summary: [SummaryRoutesEnum.storeSelector] } }]);
	}

	routeToContactPersonSelector() {
		this.popUpConfig.disableBackdropClose?.next(true);
		this.router.navigate([{ outlets: { summary: [SummaryRoutesEnum.contactPersonSelector] } }]);
	}

	private setDefaultShipTo() {
		if (this.$shipToStores().length === 1) {
			this.orderStore.setShippingAddress(this.$shipToStores()[0], 'B2B');
		} else {
			const primaryShipToStore = this.$shipToStores().find((store) => store.isPrimary);
			if (primaryShipToStore) {
				this.orderStore.setShippingAddress(primaryShipToStore, 'B2B');
			}
		}
	}
}
